import type { IncidentCardPriority, IncidentCardStatus } from '@hivewatch/dark'

import type { IncidentOrigin } from '@/features/incidents-table'
import {
  DeviceType,
  IncidentPriority,
  IncidentSource,
  IncidentStatus,
  IncidentType,
  QuickReportType,
} from '@/graphql/generated/schemas'

import { formatFullName } from './formats'

export enum IncidentTypeSlug {
  DoorForcedOpen = 'door-forced-open',
  DoorUnlockedTailgate = 'door-unlocked-tailgate',
  DeviceHealth = 'device-health',
  DoorHeldOpenStarted = 'door-held-open-started',
  DeviceOffline = 'device-offline',
  LineCrossing = 'line-crossing',
  Alarm = 'alarm',
  DoorOpenAlarm = 'door-open-alarm',
}

/**
 * Canonical ITD tailgating slug plus the legacy aliases older ITDs may still
 * carry. Shared by the incident drawer's getIsTailgatingIncident and the Grid
 * Testing Zone's per-incident mechanism routing (ENG-34040).
 */
export const TAILGATING_INCIDENT_SLUGS: string[] = [
  IncidentTypeSlug.DoorUnlockedTailgate,
  IncidentType.Tailgating,
  'tailgating',
]

/**
 * Structural tailgating check for lightweight incident shapes (e.g. Grid
 * Testing Zone search rows) that don't carry the full drawer incident. Under
 * ITDs the discriminator is the incidentType slug (`door-unlocked-tailgate`),
 * with a fallback to the legacy IncidentType enum. Mirrors the drawer's
 * getIsTailgatingIncident minus its missing-slug Sentry reporting — the
 * drawer owns that signal.
 */
export const isTailgatingIncidentLike = (
  incident: { type?: IncidentType | null; incidentTypeSlug?: string | null },
  isIncidentTypeDefinition: boolean
): boolean => {
  const slug = isIncidentTypeDefinition ? incident.incidentTypeSlug : null
  if (slug != null) return TAILGATING_INCIDENT_SLUGS.includes(slug)
  return incident.type === IncidentType.Tailgating
}

export const getIncidentStatusColor = (status: IncidentStatus) => {
  switch (status) {
    case IncidentStatus.Resolved:
      return 'incident.resolved.100'
    case IncidentStatus.Active:
      return 'incident.active.100'
    case IncidentStatus.InProgress:
      return 'incident.progress.100'
    default:
      return '#888888'
  }
}

export const getIncidentStatusColorHex = (status: IncidentStatus) => {
  switch (status) {
    case IncidentStatus.Resolved:
      return '#29D11E'
    case IncidentStatus.Active:
      return '#D01030'
    case IncidentStatus.InProgress:
      return '#D16A1E'
    default:
      return '#888888'
  }
}

export const getIncidentStatusDisplayName = (
  status: IncidentStatus,
  isFlagged?: boolean
) => {
  if (isFlagged) {
    return 'Flagged'
  }
  switch (status) {
    case IncidentStatus.Resolved:
      return 'Resolved'
    case IncidentStatus.Active:
      return 'Active'
    case IncidentStatus.InProgress:
      return 'In Progress'
    default:
      return 'Unknown'
  }
}
export const getIncidentSourceDisplayName = (source: IncidentOrigin) => {
  switch (source.type) {
    case DeviceType.Alarm:
      return source.name
    case DeviceType.Camera:
      return source.name
    case DeviceType.Door:
      return source.name
    case IncidentSource.QuickReport:
    case IncidentSource.CustomQuickReport:
      return 'QuickReport'
    case IncidentSource.Guard:
      return 'Guard'
    case IncidentSource.Sensoscientific:
      return 'Sensoscientific'
    case IncidentSource.HoneywellMassComms:
      return 'Honeywell Mass Comms'
    case IncidentSource.Email:
      return 'Email'
    default:
      return 'Manual'
  }
}

/** Incident sources that originate from an ingested third-party alert email. */
export const isEmailSourcedIncident = (
  source?: IncidentSource | null
): boolean =>
  source === IncidentSource.Sensoscientific ||
  source === IncidentSource.HoneywellMassComms

/**
 * An Email Incident: created from an email sent to an Email Source's Ingest
 * Address (RFC0197). It has no device, so no camera clip, and carries the
 * Original Email. Not the same as isEmailSourcedIncident, whose sources send
 * their alerts through a fixed integration.
 */
export const isIngestedEmailIncident = (
  incident?: {
    source?: IncidentSource | null
    email?: object | null
  } | null
): boolean =>
  incident?.source === IncidentSource.Email || incident?.email != null

/**
 * Card-title label for email-derived incident sources. Null for every other
 * source so callers can fall back to the incident name.
 */
export const getEmailIncidentSourceLabel = (
  source?: IncidentSource | null
): string | null => {
  switch (source) {
    case IncidentSource.Sensoscientific:
      return 'Sensoscientific'
    case IncidentSource.HoneywellMassComms:
      return 'Honeywell Mass Comms'
    default:
      return null
  }
}

/**
 * An incident is manual when it belongs to a manual incident type. Two
 * generations of the type system encode that: the legacy
 * manualIncidentCategory row (object or id scalar), and the incident type
 * definition's `manual` field — the only marker for ITD-created types, which
 * have no category row (ENG-33453). Callers should pass `incidentType` when
 * their query selects it; without it, ITD-only manual incidents classify as
 * device incidents.
 */
export const isManualIncident = (incident: {
  manualIncidentCategory?: { id: string } | null
  manualIncidentCategoryId?: string | null
  incidentType?: { manual?: boolean | null } | null
}): boolean =>
  !!(incident?.manualIncidentCategory || incident?.manualIncidentCategoryId) ||
  incident?.incidentType?.manual === true

/**
 * A Custom Quick Report incident carries its captured data as structured form
 * answers (quickReportDetails.displayAnswers), which the drawer lists as the
 * intake section. The stored description is kept out of the card for these
 * incidents: when no form question is designated as the description, the
 * backend stores every answer as "label: value" lines, which would repeat the
 * intake list (ENG-34056). The report type is the primary marker; the source
 * is the fallback for queries that do not select quickReportDetails.
 */
export const isCustomQuickReportIncident = (
  incident?: {
    source?: IncidentSource | null
    quickReportDetails?: { reportType?: QuickReportType | null } | null
  } | null
): boolean =>
  incident?.quickReportDetails?.reportType === QuickReportType.Custom ||
  incident?.source === IncidentSource.CustomQuickReport

export const isAIOperator = (obj: {
  firstName?: string
  lastName?: string
}): boolean => {
  return formatFullName(obj) === 'AI Operator'
}

export const getIncidentPriorityColor = (
  priority?: IncidentPriority | null
) => {
  switch (priority) {
    case IncidentPriority.Critical:
      return '#F16974'
    case IncidentPriority.High:
      return '#fae8eb'
    case IncidentPriority.Medium:
      return '#FFF0E5'
    case IncidentPriority.Low:
      return '#e8fae9'
    default:
      return 'gray.100'
  }
}

export const getIncidentPriorityDisplayName = (
  priority?: IncidentPriority | null
) => {
  switch (priority) {
    case IncidentPriority.Critical:
      return 'Critical'
    case IncidentPriority.High:
      return 'High'
    case IncidentPriority.Medium:
      return 'Medium'
    case IncidentPriority.Low:
      return 'Low'
    default:
      return 'No Priority'
  }
}

// Bridges between the schema enums and the dark package's IncidentCard
// prop unions. Shared by incident-drawer and stale-work.
export const toCardStatus = (
  status: IncidentStatus | null | undefined
): IncidentCardStatus => {
  switch (status) {
    case IncidentStatus.InProgress:
      return 'in-progress'
    case IncidentStatus.Resolved:
      return 'resolved'
    default:
      return 'active'
  }
}

export const toCardPriority = (
  priority: IncidentPriority | null | undefined
): IncidentCardPriority => {
  switch (priority) {
    case IncidentPriority.Low:
      return 'low'
    case IncidentPriority.Medium:
      return 'medium'
    case IncidentPriority.High:
      return 'high'
    case IncidentPriority.Critical:
      return 'urgent'
    default:
      return 'none'
  }
}
